import React, { useEffect, useId, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { useT } from '../../i18n/LanguageContext';
import {
  DISTRIBUTOR_PASSWORD_PATTERN,
  DISTRIBUTOR_PASSWORD_REQUIREMENT,
} from '../../utils/distributorPassword';

const unwrap = (response) => response?.data?.data;

export function IndependentPortalLogin({ distributor, admin, workspaceAdmin = false }) {
  const t = useT();
  const passwordHelpId = useId();
  const { busy, setBusy, setError, setNotice, load } = admin;
  const empty = { firstName: '', lastName: '', email: '', username: '', password: '' };
  const [master, setMaster] = useState(empty);
  const [confirmed, setConfirmed] = useState(false);
  const mounted = useRef(true);
  const pending = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const create = async (event) => {
    event.preventDefault();
    if (pending.current || busy || !confirmed) return;
    if (!DISTRIBUTOR_PASSWORD_PATTERN.test(master.password)) {
      setError(DISTRIBUTOR_PASSWORD_REQUIREMENT);
      return;
    }
    const session = captureSession();
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const receipt = unwrap(
        await (workspaceAdmin
          ? distributorService.createWorkspaceAdmin({ master })
          : distributorService.addDistributorRep(distributor._id, { master }))
      );
      if (!mounted.current || !isSessionCurrent(session)) return;
      if (!receipt?._id) throw new Error('Invalid portal login');
      setMaster(empty);
      setConfirmed(false);
      await load();
      if (!mounted.current || !isSessionCurrent(session)) return;
      setNotice(
        workspaceAdmin
          ? 'Distributor administrator created. Sign in through Distributor login.'
          : 'Portal login created. The user must accept Terms and Privacy after signing in.'
      );
    } catch {
      if (!mounted.current || !isSessionCurrent(session)) return;
      setError(
        'Portal login could not be confirmed. Refresh the distributor list before trying again.'
      );
    } finally {
      pending.current = false;
      if (mounted.current && isSessionCurrent(session)) setBusy(false);
    }
  };
  return (
    <section
      className="me-distributor-admin-card space-y-5"
      aria-label={workspaceAdmin ? 'Add distributor administrator' : 'Add portal login'}
    >
      <h2 className="text-lg font-semibold text-slate-900">
        {workspaceAdmin ? 'Add distributor administrator' : 'Add portal login'}
      </h2>
      <p className="text-sm text-slate-600">
        {workspaceAdmin
          ? 'Manages all distributors, including future distributors. Has no access to the MedEffects client workspace.'
          : `This login shares ${distributor.name}'s distributor customers and product bank. A direct MedEffects account or clinic assignment is not required.`}
      </p>
      <form onSubmit={create} className="me-distributor-admin-form space-y-4">
        <fieldset disabled={busy} className="grid gap-4 sm:grid-cols-2">
          {Object.entries({
            firstName: 'First name',
            lastName: 'Last name',
            email: 'Contact email',
            username: 'Portal username',
            password: workspaceAdmin ? 'Password' : 'Temporary password',
          }).map(([key, label]) => (
            <label key={key}>
              {label}
              <input
                required
                className="mt-1 block w-full rounded border p-2"
                type={key === 'password' ? 'password' : key === 'email' ? 'email' : 'text'}
                autoComplete={key === 'password' ? 'new-password' : 'off'}
                autoCapitalize={key === 'username' ? 'none' : undefined}
                minLength={key === 'password' ? 12 : key === 'username' ? 3 : undefined}
                maxLength={
                  key === 'password' ? 72 : key === 'username' ? 32 : key === 'email' ? 254 : 100
                }
                pattern={
                  key === 'password'
                    ? DISTRIBUTOR_PASSWORD_PATTERN.source
                    : key === 'username'
                      ? '[a-zA-Z][a-zA-Z0-9._-]{2,31}'
                      : undefined
                }
                title={key === 'password' ? t(DISTRIBUTOR_PASSWORD_REQUIREMENT) : undefined}
                aria-describedby={key === 'password' ? passwordHelpId : undefined}
                value={master[key]}
                onChange={(event) =>
                  setMaster((current) => ({ ...current, [key]: event.target.value }))
                }
              />
            </label>
          ))}
          <p id={passwordHelpId} className="text-xs text-slate-600 sm:col-span-2">
            {t(DISTRIBUTOR_PASSWORD_REQUIREMENT)}
          </p>
        </fieldset>
        <label className="me-distributor-admin-confirm flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={confirmed}
            disabled={busy}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          {workspaceAdmin
            ? 'I verified this person should administer all distributors'
            : "I verified this person should share this distributor's customers and product bank"}
        </label>
        <button
          type="submit"
          disabled={busy || !confirmed}
          className="me-distributor-admin-primary"
        >
          {workspaceAdmin ? 'Create distributor administrator' : 'Create portal login'}
        </button>
      </form>
    </section>
  );
}

export default function DistributorRepSetup({ distributor, admin }) {
  const t = useT();
  const passwordHelpId = useId();
  const { busy, setBusy, setError, setNotice, load } = admin;
  const [repSearch, setRepSearch] = useState('');
  const [repOptions, setRepOptions] = useState(null);
  const [selectedRep, setSelectedRep] = useState(null);
  const [repUsername, setRepUsername] = useState('');
  const [repPassword, setRepPassword] = useState('');
  const [repConfirmed, setRepConfirmed] = useState(false);

  const findRep = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setSelectedRep(null);
    setRepConfirmed(false);
    if (repSearch.trim().length < 2) {
      setError('Enter at least two characters to search sales reps.');
      return;
    }
    setBusy(true);
    try {
      const rows = unwrap(await distributorService.adminDistributorClientOptions(repSearch.trim()));
      if (!Array.isArray(rows)) throw new Error('Invalid client accounts');
      setRepOptions(rows.filter((row) => row.role === 'sales_rep'));
    } catch {
      setError('Sales rep search is unavailable. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const addRep = async (event) => {
    event.preventDefault();
    if (!distributor?.sourceClientUser || !selectedRep || !repConfirmed) return;
    if (!DISTRIBUTOR_PASSWORD_PATTERN.test(repPassword)) {
      setError(DISTRIBUTOR_PASSWORD_REQUIREMENT);
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    let saved = false;
    try {
      const receipt = unwrap(
        await distributorService.addDistributorRep(distributor._id, {
          sourceClientUserId: selectedRep._id,
          username: repUsername.trim().toLowerCase(),
          password: repPassword,
        })
      );
      if (!receipt?._id) throw new Error('Invalid login receipt');
      saved = true;
      setRepPassword('');
      setRepUsername('');
      setRepConfirmed(false);
      setSelectedRep(null);
      setRepOptions(null);
      setRepSearch('');
      await load();
      setNotice('Sales rep login created. The rep must accept Terms and Privacy after signing in.');
    } catch {
      try {
        await load();
      } catch {
        /* Keep the uncertain-result message. */
      }
      setError(
        saved
          ? 'Login was created, but the list could not refresh. Reload before continuing.'
          : 'Login could not be confirmed. Review existing logins before trying again.'
      );
    } finally {
      setBusy(false);
    }
  };

  if (distributor.independentCustomers)
    return <IndependentPortalLogin distributor={distributor} admin={admin} />;

  return (
    <section className="me-distributor-admin-card space-y-5" aria-label="Add sales rep login">
      <div className="me-distributor-admin-card-heading">
        <span className="me-distributor-admin-step" aria-hidden="true">
          03
        </span>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Add a sales rep login</h2>
          <p className="mt-1 text-sm text-slate-600">
            Give another sales rep a separate portal username. Their clinic access follows their own
            MedEffects sales-rep account; the product bank remains shared by {distributor.name}.
          </p>
        </div>
      </div>
      <form onSubmit={findRep} className="me-distributor-admin-search">
        <label className="min-w-0 flex-1">
          Find sales rep by name or email
          <input
            className="mt-1 block w-full rounded border p-2"
            value={repSearch}
            maxLength={100}
            onChange={(event) => setRepSearch(event.target.value)}
          />
        </label>
        <button type="submit" disabled={busy} className="me-distributor-admin-secondary">
          Find sales reps
        </button>
      </form>
      {repOptions?.length === 0 && <p className="text-sm text-slate-600">No sales reps matched.</p>}
      {repOptions?.length > 0 && (
        <ul className="me-distributor-admin-results divide-y">
          {repOptions.map((row) => (
            <li
              key={row._id}
              className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm"
            >
              <span>
                <strong>
                  {row.firstName} {row.lastName}
                </strong>{' '}
                · {row.email}
                <span className="block text-slate-600">{row.clinics.length} assigned clinics</span>
              </span>
              <button
                type="button"
                className="me-distributor-admin-text-button"
                disabled={
                  busy ||
                  row.clinics.length === 0 ||
                  distributor.masters.some((master) => master.sourceClientUserId === row._id)
                }
                onClick={() => {
                  setSelectedRep(row);
                  setRepConfirmed(false);
                }}
              >
                Use sales rep
              </button>
            </li>
          ))}
        </ul>
      )}
      {selectedRep && (
        <form onSubmit={addRep} className="me-distributor-admin-form space-y-4">
          <p className="me-distributor-admin-selection text-sm">
            <strong>
              {selectedRep.firstName} {selectedRep.lastName}
            </strong>{' '}
            · {selectedRep.email} · {selectedRep.clinics.length} assigned clinics
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <label>
              Portal username
              <input
                required
                minLength={3}
                maxLength={32}
                pattern="[a-zA-Z][a-zA-Z0-9._-]{2,31}"
                autoCapitalize="none"
                autoComplete="off"
                spellCheck={false}
                className="mt-1 block w-full rounded border p-2"
                value={repUsername}
                onChange={(event) => setRepUsername(event.target.value)}
              />
            </label>
            <label>
              Temporary password
              <input
                required
                type="password"
                autoComplete="new-password"
                minLength={12}
                maxLength={72}
                pattern={DISTRIBUTOR_PASSWORD_PATTERN.source}
                title={t(DISTRIBUTOR_PASSWORD_REQUIREMENT)}
                aria-describedby={passwordHelpId}
                className="mt-1 block w-full rounded border p-2"
                value={repPassword}
                onChange={(event) => setRepPassword(event.target.value)}
              />
            </label>
          </div>
          <p id={passwordHelpId} className="text-xs text-slate-600">
            {t(DISTRIBUTOR_PASSWORD_REQUIREMENT)}
          </p>
          <label className="me-distributor-admin-confirm flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={repConfirmed}
              disabled={busy}
              onChange={(event) => setRepConfirmed(event.target.checked)}
            />
            I verified this sales rep, their clinics, and their separate portal username
          </label>
          <button
            type="submit"
            disabled={busy || !repConfirmed}
            className="me-distributor-admin-primary"
          >
            Create sales rep login
          </button>
        </form>
      )}
    </section>
  );
}
